/**
 * @module cookies
 *
 * @description
 * Modulo centralizzato per la gestione dei cookie dell'applicazione.
 * Fornisce configurazioni predefinite, helper tipizzati e documentazione
 * per ogni cookie utilizzato nel progetto.
 *
 * ## Architettura
 * Ogni cookie è registrato in `COOKIE_REGISTRY` con:
 * - `name`: il nome del cookie (o un pattern per cookie dinamici)
 * - `description`: a cosa serve
 * - `options`: opzioni di default applicate alla creazione
 *
 * ## Opzioni Cookie — Documentazione
 *
 * | Opzione      | Tipo              | Descrizione                                                                                                                                                       |
 * |--------------|-------------------|-------------------------------------------------------------------------------------------------------------------------------------------------------------------|
 * | `path`       | `string`          | Il percorso URL per cui il cookie è valido. `/` lo rende accessibile su tutto il dominio.                                                                         |
 * | `maxAge`     | `number`          | Durata in **secondi** dalla creazione. Dopo questo tempo il browser elimina il cookie. Alternativa a `expires`.                                                   |
 * | `expires`    | `Date`            | Data di scadenza assoluta. Il browser elimina il cookie dopo questa data. Alternativa a `maxAge`.                                                                 |
 * | `domain`     | `string`          | Dominio per cui il cookie è valido (e.g. `.example.com` include tutti i sottodomini). Se omesso vale solo per il dominio corrente.                                |
 * | `secure`     | `boolean`         | Se `true`, il cookie viene inviato solo su connessioni HTTPS. Raccomandato per dati sensibili (token, sessioni).                                                  |
 * | `sameSite`   | `'strict' \| 'lax' \| 'none'` | Controlla l'invio cross-site: `strict` = solo same-site, `lax` = same-site + navigazione top-level, `none` = sempre (richiede `secure`).        |
 * | `httpOnly`   | `boolean`         | Se `true`, il cookie NON è accessibile via JavaScript (`document.cookie`). Usato lato server per proteggere token da XSS. **Non applicabile client-side.**        |
 * | `encode`     | `(value: string) => string` | Funzione di encoding del valore. Default: `encodeURIComponent`. Impostare `(v) => v` per disabilitare l'encoding (necessario per token opachi).       |
 *
 * ## Cookie Registrati
 *
 * ### `cc-nx_{siteId}` — Refresh Token SCAPI (registered user)
 * Contiene il refresh_token SLAS per utenti registrati.
 * - **Valore**: stringa opaca URL-safe (alfanumerica + `-` + `_` + `.`), ~40-100 caratteri.
 *   Generato dal SLAS /token endpoint, NON è un JWT.
 * - **Encoding**: DISABILITATO — il token è già URL-safe e l'encoding potrebbe
 *   rompere la validazione durante il refresh flow.
 * - **Durata**: 30 giorni (registrato) — configurabile da `refresh_token_expires_in` nella response.
 * - **Secure**: `true` — contiene credenziali di sessione.
 *
 * ### `cc-nx-g_{siteId}` — Refresh Token SCAPI (guest)
 * Come sopra ma per utenti guest. Viene rimosso al login.
 * - **Durata**: 7 giorni (guest).
 *
 * ### `bc_fcountry` — Paese selezionato (locale modal)
 * Persiste la selezione del paese dell'utente dal locale modal.
 * - **Valore**: codice ISO 3166-1 alpha-2 del paese (e.g. `IT`, `US`, `FR`).
 *   NON la label localizzata (e.g. non "Italia" ma "IT").
 * - **Durata**: 10 giorni.
 *
 * ### `bc_countryselection` — Conferma selezione paese (switch locale modal)
 * Indica che l'utente ha interagito con lo switch locale modal.
 * - **Valore**: codice ISO 3166-1 alpha-2 del paese (e.g. `IT`, `US`).
 * - **Durata**: sessione (nessun maxAge/expires — scade alla chiusura del browser).
 *
 * ### `sticky-banner` (cookieID dinamico da Page Designer)
 * Gestito dal componente StickyBanner. La validazione sarà implementata lato SFRA.
 * Non incluso in questo modulo — rimane gestito localmente nel componente.
 *
 * ### `_dyid_server` — Dynamic Yield ID (server-side)
 * Cookie di sincronizzazione per il tracking Dynamic Yield.
 * - **Valore**: stringa numerica (ID utente DY).
 * - **Durata**: 1 anno.
 * - **Secure**: `true`.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Opzioni per la creazione di un cookie.
 * Mappa 1:1 con le opzioni standard dei cookie HTTP.
 */
export interface CookieOptions {
    /** Percorso URL per cui il cookie è valido. Default: '/' */
    path?: string
    /** Durata in secondi dalla creazione */
    maxAge?: number
    /** Data di scadenza assoluta */
    expires?: Date
    /** Dominio di validità (e.g. '.example.com') */
    domain?: string
    /** Invia solo su HTTPS */
    secure?: boolean
    /** Politica cross-site: 'strict' | 'lax' | 'none' */
    sameSite?: 'strict' | 'lax' | 'none'
    /** Funzione di encoding del valore. Default: encodeURIComponent */
    encode?: (value: string) => string
}

/**
 * Configurazione di un cookie registrato nel sistema.
 */
export interface CookieConfig {
    /** Nome del cookie (o pattern descrittivo per cookie dinamici) */
    name: string
    /** Descrizione del cookie */
    description: string
    /** Opzioni di default applicate alla creazione */
    options: CookieOptions
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Un giorno in secondi */
const ONE_DAY_SECONDS = 86400

/** TTL di default per il refresh token guest SLAS (7 giorni) */
export const SLAS_REFRESH_TOKEN_GUEST_TTL = ONE_DAY_SECONDS * 7

/** TTL di default per il refresh token registered SLAS (30 giorni) */
export const SLAS_REFRESH_TOKEN_REGISTERED_TTL = ONE_DAY_SECONDS * 30

/** TTL per il cookie bc_fcountry (10 giorni) */
export const BC_FCOUNTRY_TTL = ONE_DAY_SECONDS * 10

/** TTL per il cookie _dyid_server (1 anno) */
export const DY_ID_SERVER_TTL = ONE_DAY_SECONDS * 365

/**
 * Identity function — disabilita l'encoding del valore cookie.
 * Usata per token opachi che sono già URL-safe.
 */
export const noEncode = (value: string): string => value

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

/**
 * Registro centralizzato di tutti i cookie gestiti dall'applicazione.
 * Usato come source of truth per nomi, descrizioni e opzioni di default.
 */
export const COOKIE_REGISTRY = {
    /**
     * Refresh token SCAPI per utente registrato.
     * Il valore è una stringa opaca URL-safe restituita dall'endpoint SLAS /token.
     */
    SLAS_REFRESH_TOKEN: {
        name: 'cc-nx_',
        description: 'Refresh token SLAS per utente registrato. Stringa opaca URL-safe, nessun encoding.',
        options: {
            path: '/',
            secure: true,
            sameSite: 'lax' as const,
            encode: noEncode,
        },
    },

    /**
     * Refresh token SCAPI per utente guest.
     * Rimosso al login dell'utente.
     */
    SLAS_REFRESH_TOKEN_GUEST: {
        name: 'cc-nx-g_',
        description: 'Refresh token SLAS per utente guest. Rimosso al login.',
        options: {
            path: '/',
            secure: true,
            sameSite: 'lax' as const,
            encode: noEncode,
        },
    },

    /**
     * Paese selezionato dall'utente tramite il locale modal.
     * Valore: codice ISO 3166-1 alpha-2 (e.g. "IT", "US").
     */
    BC_FCOUNTRY: {
        name: 'bc_fcountry',
        description: 'Paese selezionato - codice ISO 3166-1 alpha-2 (e.g. IT, US, FR).',
        options: {
            path: '/',
            maxAge: BC_FCOUNTRY_TTL,
        },
    },

    /**
     * Conferma interazione con lo switch locale modal.
     * Valore: codice ISO 3166-1 alpha-2.
     * Sessione: scade alla chiusura del browser.
     */
    BC_COUNTRYSELECTION: {
        name: 'bc_countryselection',
        description: 'Conferma selezione paese dallo switch locale modal - codice ISO alpha-2. Cookie di sessione.',
        options: {
            path: '/',
        },
    },

    /**
     * Dynamic Yield server-side ID per tracking cross-request.
     */
    DY_ID_SERVER: {
        name: '_dyid_server',
        description: 'Dynamic Yield server-side user ID per sincronizzazione tracking.',
        options: {
            path: '/',
            maxAge: DY_ID_SERVER_TTL,
            secure: true,
        },
    },

    /**
     * Cookie dello sticky banner (nome dinamico da Page Designer).
     * Il nome viene sanitizzato runtime tramite `sanitizeCookieName`.
     * Valore: 'true' (flag di dismissione).
     * Durata: configurabile da PD, default 365 giorni.
     */
    STICKY_BANNER: {
        name: '[dynamic-from-page-designer]',
        description: 'Flag dismissione sticky banner. Nome dinamico, sanitizzato a runtime. Valore: "true".',
        options: {
            path: '/',
            maxAge: ONE_DAY_SECONDS * 365,
        },
    },
} as const satisfies Record<string, CookieConfig>

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Converte secondi in una Date futura a partire da adesso.
 */
export const secondsToExpiry = (seconds: number): Date => {
    const date = new Date()
    date.setSeconds(date.getSeconds() + seconds)
    return date
}

/**
 * Serializza le opzioni cookie in una stringa `document.cookie` compatibile.
 *
 * @example
 * ```ts
 * document.cookie = serializeCookie('myCookie', 'value', { path: '/', maxAge: 3600 })
 * ```
 */
export const serializeCookie = (name: string, value: string, options: CookieOptions = {}): string => {
    const {path, maxAge, expires, domain, secure, sameSite, encode = encodeURIComponent} = options
    const encodedValue = encode(value)

    let cookieStr = `${name}=${encodedValue}`

    if (path) cookieStr += `; path=${path}`
    if (maxAge !== undefined) cookieStr += `; max-age=${maxAge}`
    if (expires) cookieStr += `; expires=${expires.toUTCString()}`
    if (domain) cookieStr += `; domain=${domain}`
    if (secure) cookieStr += `; Secure`
    if (sameSite) cookieStr += `; SameSite=${sameSite.charAt(0).toUpperCase() + sameSite.slice(1)}`

    return cookieStr
}

/**
 * Imposta un cookie via `document.cookie` utilizzando le opzioni centralizzate.
 * Unisce le opzioni di default del registry con eventuali override.
 *
 * @param name - Nome del cookie
 * @param value - Valore da salvare
 * @param options - Override opzionali sulle opzioni di default
 */
export const setCookie = (name: string, value: string, options: CookieOptions = {}): void => {
    if (typeof document === 'undefined') return
    document.cookie = serializeCookie(name, value, options)
}

/**
 * Rimuove un cookie impostando la scadenza nel passato.
 */
export const removeCookie = (name: string, path = '/'): void => {
    if (typeof document === 'undefined') return
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`
}

/**
 * Legge il valore di un cookie per nome da `document.cookie`.
 * Restituisce `undefined` se il cookie non esiste.
 */
export const getCookie = (name: string): string | undefined => {
    if (typeof document === 'undefined') return undefined
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : undefined
}

/**
 * Legge il valore di un cookie senza decodificarlo.
 * Utile per token opachi che non devono essere decodificati.
 */
export const getCookieRaw = (name: string): string | undefined => {
    if (typeof document === 'undefined') return undefined
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`))
    return match ? match[1] : undefined
}

// ---------------------------------------------------------------------------
// Domain-specific helpers
// ---------------------------------------------------------------------------

/**
 * Imposta il cookie del refresh token SLAS (registered user).
 *
 * @param siteId - ID del sito corrente
 * @param refreshToken - Token opaco restituito dall'endpoint SLAS /token
 * @param ttlSeconds - TTL in secondi (default: 30 giorni)
 */
export const setSlasRefreshToken = (siteId: string, refreshToken: string, ttlSeconds = SLAS_REFRESH_TOKEN_REGISTERED_TTL): void => {
    const {options} = COOKIE_REGISTRY.SLAS_REFRESH_TOKEN
    const expires = secondsToExpiry(ttlSeconds)
    setCookie(`${COOKIE_REGISTRY.SLAS_REFRESH_TOKEN.name}${siteId}`, refreshToken, {
        ...options,
        expires,
    })
}

/**
 * Rimuove il cookie del refresh token SLAS guest.
 *
 * @param siteId - ID del sito corrente
 */
export const removeSlasGuestRefreshToken = (siteId: string): void => {
    removeCookie(`${COOKIE_REGISTRY.SLAS_REFRESH_TOKEN_GUEST.name}${siteId}`)
}

/**
 * Imposta il cookie `bc_fcountry` con un codice ISO.
 *
 * @param countryCode - Codice ISO 3166-1 alpha-2 (e.g. "IT", "US")
 */
export const setCountryCookie = (countryCode: string): void => {
    const {name, options} = COOKIE_REGISTRY.BC_FCOUNTRY
    setCookie(name, countryCode.toUpperCase(), options)
}

/**
 * Imposta il cookie `bc_countryselection` con un codice ISO.
 * Cookie di sessione — scade alla chiusura del browser.
 *
 * @param countryCode - Codice ISO 3166-1 alpha-2 (e.g. "IT", "US")
 */
export const setCountrySelectionCookie = (countryCode: string): void => {
    const {name, options} = COOKIE_REGISTRY.BC_COUNTRYSELECTION
    setCookie(name, countryCode.toUpperCase(), options)
}

/**
 * Calcola il TTL effettivo per il refresh token considerando override, response e default.
 *
 * Priorità: overrideValue > responseValue > defaultValue
 *
 * @param overrideValue - Valore di override (e.g. configurazione applicativa)
 * @param responseValue - Valore dalla response SLAS (`refresh_token_expires_in`)
 * @param defaultValue - Valore di fallback
 */
export const getRefreshTokenTTL = (overrideValue: number, responseValue: number, defaultValue: number): number => {
    if (overrideValue) return overrideValue
    if (responseValue) return responseValue
    return defaultValue
}

// ---------------------------------------------------------------------------
// Sanitizzazione (input non controllato, e.g. Page Designer)
// ---------------------------------------------------------------------------

/**
 * Sanitizza e valida un nome di cookie proveniente da input non controllato
 * (e.g. campo cookieID di Page Designer).
 *
 * - Rimuove spazi iniziali/finali
 * - Sostituisce caratteri non validi con `-`
 * - Verifica lunghezza massima di 128 caratteri
 * - Restituisce `null` solo se il risultato è vuoto dopo la sanitizzazione
 *
 * @param rawName - Nome cookie grezzo (potenzialmente non sicuro)
 * @returns Il nome sanitizzato, oppure `null` se il risultato è vuoto
 */
export const sanitizeCookieName = (rawName: string | undefined | null): string | null => {
    if (!rawName) return null
    const trimmed = rawName.trim()
    if (!trimmed.length) return null
    // Sostituisce qualsiasi carattere non ammesso con '-'
    const sanitized = trimmed.replace(/[^a-zA-Z0-9\-_.]/g, '-')
    // Collassa sequenze di '-' consecutive
    const collapsed = sanitized.replace(/-{2,}/g, '-')
    // Rimuove '-' iniziale/finale per pulizia
    const final = collapsed.replace(/^-+|-+$/g, '')
    if (!final.length) return null
    // Tronca a 128 caratteri
    return final.slice(0, 128)
}

/**
 * Imposta un cookie per lo sticky banner con sanitizzazione del nome.
 * Se il cookieID non supera la validazione, il cookie NON viene creato
 * e viene emesso un warning in console.
 *
 * @param cookieID - ID del cookie da Page Designer (potenzialmente non sicuro)
 * @param cookieTimeDays - Durata in giorni (stringa da Page Designer, default: '365')
 * @returns `true` se il cookie è stato creato, `false` se il nome non è valido
 */
export const setStickyBannerCookie = (cookieID: string | undefined | null, cookieTimeDays = '365'): boolean => {
    const safeName = sanitizeCookieName(cookieID)
    if (!safeName) {
        console.warn(`[cookies] sticky-banner: cookieID non valido: "${cookieID}"`)
        return false
    }

    const days = Number(cookieTimeDays)
    const maxAge = ONE_DAY_SECONDS * days
    if (isNaN(maxAge) || maxAge <= 0) {
        console.warn(`[cookies] sticky-banner: cookieTime non valido: "${cookieTimeDays}"`)
        return false
    }

    setCookie(safeName, 'true', {path: '/', maxAge})
    return true
}

/**
 * Verifica se un cookie dello sticky banner esiste (con sanitizzazione del nome).
 *
 * @param cookieID - ID del cookie da Page Designer
 * @returns `true` se il cookie esiste, `false` altrimenti o se il nome non è valido
 */
export const hasStickyBannerCookie = (cookieID: string | undefined | null): boolean => {
    const safeName = sanitizeCookieName(cookieID)
    if (!safeName) return false
    return getCookie(safeName) !== undefined
}
